var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/hooks/useI18n.ts
import {
  DATE_FORMAT_STYLE_DMY,
  DATE_FORMAT_STYLE_MDY,
  DATE_FORMAT_STYLE_YMD
} from "@gettactic/api";
var dictionary = {
  [DATE_FORMAT_STYLE_DMY]: {
    displayDate: "dd/MM/yyyy",
    displayDateShort: "dd/MM/yy",
    displayDateWordy: "eeee, LLLL d",
    displayMonthDayYearWordy: "do MMM, yyyy",
    displayMonthDayWordy: "do LLL"
  },
  [DATE_FORMAT_STYLE_MDY]: {
    displayDate: "MM/dd/yyyy",
    displayDateShort: "MM/dd/yy",
    displayDateWordy: "eeee, LLLL do",
    displayMonthDayYearWordy: "MMM do, yyyy",
    displayMonthDayWordy: "LLL do"
  },
  [DATE_FORMAT_STYLE_YMD]: {
    displayDate: "yyyy-MM-dd",
    displayDateShort: "yy-MM-dd",
    displayDateWordy: "LLLL eeee do",
    displayMonthDayYearWordy: "yyyy do, MMM",
    displayMonthDayWordy: "do LLL"
  }
};
function useI18n(user) {
  var _a, _b, _c, _d;
  const dateFormat = user && "user" in user ? (_c = (_b = (_a = user == null ? void 0 : user.user) == null ? void 0 : _a.organization_user) == null ? void 0 : _b.date_format) != null ? _c : DATE_FORMAT_STYLE_MDY : (_d = user == null ? void 0 : user.date_format) != null ? _d : DATE_FORMAT_STYLE_MDY;
  const templates = dictionary[dateFormat];
  return { templates };
}

// src/providers/TacticHooksProvider.tsx
import { QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
var TacticHooksContext = React.createContext(null);
var TacticHooksProvider = ({
  apiClient,
  queryClient,
  children
}) => /* @__PURE__ */ jsx(
  TacticHooksContext.Provider,
  {
    value: { apiClient, queryClient, api: apiClient },
    children: /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children })
  }
);
function useTacticHooks() {
  const { apiClient, queryClient, api } = React.useContext(TacticHooksContext);
  if (apiClient === void 0 || queryClient === void 0) {
    throw new Error("useTacticHooks must be used within a TacticHooksProvider");
  }
  return {
    apiClient: apiClient.client,
    queryClient,
    api
  };
}

// src/useOrganizationQuery/index.ts
import { useQuery } from "@tanstack/react-query";
var useOrganizationQuery = () => {
  const { apiClient } = useTacticHooks();
  return useQuery(
    ["users.organizations"],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.users.organizations();
      return res.result;
    }),
    {
      staleTime: 5e3 * 60
      // 5 minutes
    }
  );
};

// src/hooks/useAllApplicablePolicies.ts
import { useQuery as useQuery2, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
var USE_LEAVE_POLICIES_QUERY_KEY = "leavePolicies";
function useAllApplicablePolicies(params, options = { keepPreviousData: true, enabled: true }) {
  const queryClient = useQueryClient();
  const { apiClient } = useTacticHooks();
  const query = useQuery2(
    [USE_LEAVE_POLICIES_QUERY_KEY, params],
    () => __async(this, null, function* () {
      const res = yield apiClient.leave.allApplicablePolicies(params);
      return res.filter((x) => !!x && !!x.policy);
    }),
    options
  );
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries([USE_LEAVE_POLICIES_QUERY_KEY]);
  }, []);
  return { query, invalidate };
}
function useApplicableRemotePolicy(params, options) {
  var _a, _b;
  const { query } = useAllApplicablePolicies(__spreadProps(__spreadValues({}, params), { type: ["remote"] }), options);
  return (_b = (_a = query.data) == null ? void 0 : _a.find((x) => {
    var _a2;
    return ((_a2 = x == null ? void 0 : x.policy) == null ? void 0 : _a2.policy_type) === "remote";
  })) != null ? _b : null;
}

// src/hooks/useAttendanceRequests.tsx
import { parseDateOnlyString } from "@gettactic/api";
import { useQuery as useQuery3 } from "@tanstack/react-query";
import { addDays, eachDayOfInterval, format } from "date-fns";
import { useMemo } from "react";
function useAttendanceRequests(params, options = { keepPreviousData: true, enabled: true }) {
  const { apiClient } = useTacticHooks();
  const commonParams = __spreadProps(__spreadValues({}, params), {
    start: params.start ? format(params.start, "yyyy-MM-dd") : void 0,
    end: params.end ? format(params.end, "yyyy-MM-dd") : void 0,
    workPolicyType: params.workPolicyType ? params.workPolicyType : [],
    status: params.status ? params.status : ["pending", "accepted"]
  });
  const dataQuery = useQuery3(
    ["attendanceRequests", commonParams],
    () => __async(this, null, function* () {
      const res = yield apiClient.leave.allRequests(commonParams);
      return res.result ? res.result.elements : [];
    }),
    options
  );
  return dataQuery;
}
function useAttendanceRequestsWeek(monday, tz) {
  const attendanceQuery = useAttendanceRequests({
    start: monday,
    end: addDays(monday, 7),
    tz
  });
  const attendanceByDay = useMemo(() => {
    var _a;
    const requests = (_a = attendanceQuery.data) != null ? _a : [];
    const weekDayRequests = {};
    requests.forEach((request) => {
      eachDayOfInterval({
        start: parseDateOnlyString(request.starts),
        end: parseDateOnlyString(request.ends)
      }).forEach((date) => {
        const current = weekDayRequests[format(date, "yyyy-MM-dd")];
        if (!current || current.starts > request.starts) {
          weekDayRequests[format(date, "yyyy-MM-dd")] = request;
        }
      });
    });
    return weekDayRequests;
  }, [attendanceQuery.data]);
  return { attendanceQuery, attendanceByDay };
}

// src/hooks/useWorkingLocations.ts
import { useInfiniteQuery } from "@tanstack/react-query";
function useWorkingLocations(filters, options = { keepPreviousData: true, enabled: true }) {
  const { apiClient } = useTacticHooks();
  if (!filters.limit) {
    filters.limit = 100;
  }
  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status } = useInfiniteQuery(
    ["schedules", "workingLocations", filters],
    (_0) => __async(this, [_0], function* ({ pageParam: cursor }) {
      if (cursor) {
        filters.cursor = cursor;
      }
      const res = yield apiClient.schedules.workingLocations(filters);
      return res.result;
    }),
    {
      getNextPageParam: (lastPage) => {
        var _a, _b, _c;
        const all = (_a = lastPage == null ? void 0 : lastPage.days) != null ? _a : {};
        const days = Object.keys(all);
        let maxLength = 0;
        const lastCursor = lastPage == null ? void 0 : lastPage.cursor;
        for (let i = 0; i < days.length; i++) {
          const day = days[i];
          const dayLength = (_b = all[day].length) != null ? _b : 0;
          if (dayLength > maxLength) {
            maxLength = dayLength;
          }
        }
        return maxLength < ((_c = filters.limit) != null ? _c : 0) ? void 0 : lastCursor != null ? lastCursor : void 0;
      }
    }
  );
  return { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status };
}

// src/hooks/useDivisionsMenuQuery.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";
function byOfficeIdBuilder(divisionsTree) {
  const map = /* @__PURE__ */ new Map();
  const visitNode = (node) => {
    if ("offices" in node) {
      for (const office of node.offices) {
        map.set(office.id, node);
      }
    }
    if ("children" in node) {
      for (const child of node.children) {
        visitNode(child);
      }
    }
  };
  if (Array.isArray(divisionsTree)) {
    for (const node of divisionsTree) {
      visitNode(node);
    }
  } else {
    visitNode(divisionsTree);
  }
  return map;
}
var useDivisionsMenuQuery = () => {
  const { apiClient } = useTacticHooks();
  return useQuery4(
    ["useDivisionsMenuQuery", apiClient.slug],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.divisions.withOffices();
      const result = res.result;
      return result ? {
        tree: result,
        byOfficeId: byOfficeIdBuilder(result)
      } : void 0;
    }),
    {
      staleTime: 5e3 * 60,
      enabled: !!apiClient.slug
    }
  );
};

// src/hooks/useDivisionsQuery.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
import { useMemo as useMemo2 } from "react";
var useDivisionsQuery = (filter = {}) => {
  const { apiClient } = useTacticHooks();
  const divisions = useQuery5(
    ["useDivisionsQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.divisions.all(filter);
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  const computed = useMemo2(() => {
    var _a, _b, _c;
    const hasDivisions = ((_b = (_a = divisions.data) == null ? void 0 : _a.length) != null ? _b : 0) > 0;
    const divisionsOptions = ((_c = divisions.data) != null ? _c : []).map((x) => ({
      label: x.name,
      parent_id: x.parent_id,
      value: x.id
    }));
    divisionsOptions.unshift({
      label: "-- No division --",
      value: "",
      parent_id: ""
    });
    return {
      hasDivisions,
      divisionsOptions
    };
  }, [divisions.data]);
  return __spreadValues({
    divisions
  }, computed);
};

// src/hooks/useOfficesQuery.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
var useOfficesQuery = () => {
  const { apiClient } = useTacticHooks();
  return useQuery6(
    ["useOfficesQuery", apiClient.slug],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.organizations.offices();
      return res.result;
    }),
    {
      staleTime: 5e3 * 60,
      enabled: !!apiClient.slug
    }
  );
};

// src/hooks/usePageSectionsQuery.ts
import { useQuery as useQuery7 } from "@tanstack/react-query";
var usePageSectionsQuery = (filter) => {
  const { apiClient } = useTacticHooks();
  const sections = useQuery7(
    ["usePageSectionsQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.pages.getSections();
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  return sections.data;
};

// src/hooks/usePagesQuery.ts
import { useQuery as useQuery8 } from "@tanstack/react-query";
var usePagesQuery = (filter) => {
  const { apiClient } = useTacticHooks();
  const sections = useQuery8(
    ["usePageQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.pages.getPages();
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  return sections.data;
};

// src/providers/AppAuthenticatedProvider.tsx
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import Cookies from "js-cookie";
import React2, { useEffect, useReducer } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var AppAuthenticatedContext = React2.createContext(void 0);
function userContextReducer(state, action) {
  var _a, _b;
  switch (action.type) {
    case "updateAuthUser": {
      return __spreadProps(__spreadValues({}, state), {
        authenticatedUser: action.payload
      });
    }
    case "updateCurrentOffice": {
      const officeMatch = (((_b = (_a = state.authenticatedUser) == null ? void 0 : _a.offices) == null ? void 0 : _b.offices) || []).find((x) => x.id === action.payload);
      if (!officeMatch) {
        throw new Error("Office not found");
      }
      Cookies.set("__selected__office", action.payload);
      return __spreadProps(__spreadValues({}, state), {
        currentOffice: officeMatch
      });
    }
    case "updateDeviceTz": {
      return __spreadProps(__spreadValues({}, state), {
        deviceTz: action.payload
      });
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
}
var AppAuthenticatedProvider = ({ authenticatedUser, children, api, cookieOffice: forceOffice }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  useEffect(() => {
    action({ type: "updateAuthUser", payload: authenticatedUser });
  }, [authenticatedUser]);
  if (authenticatedUser) {
    api.updateUser(authenticatedUser);
  }
  const cookieOffice = forceOffice || Cookies.get("__selected__office");
  const selectedCookieOffice = (_a = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _a.offices.find(
    (id) => id.id === cookieOffice
  );
  const defaultOrFirstOffice = selectedCookieOffice || (((_b = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _b.officeId) ? (_d = (_c = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _c.offices) == null ? void 0 : _d.find(
    (x) => {
      var _a2;
      return x.id === ((_a2 = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _a2.officeId);
    }
  ) : (authenticatedUser == null ? void 0 : authenticatedUser.offices) ? authenticatedUser == null ? void 0 : authenticatedUser.offices.office : (_e = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _e.offices[0]);
  if (!defaultOrFirstOffice) {
    throw new Error("No office found");
  }
  const [state, action] = useReducer(userContextReducer, {
    authenticatedUser,
    deviceTz: (_i = (_h = (_f = getDeviceTZ()) != null ? _f : defaultOrFirstOffice.time_zone) != null ? _h : (_g = authenticatedUser == null ? void 0 : authenticatedUser.user) == null ? void 0 : _g.time_zone) != null ? _i : "UTC",
    currentOffice: defaultOrFirstOffice
  });
  const value = {
    userContext: state,
    setUserContext: action
  };
  return /* @__PURE__ */ jsx2(AppAuthenticatedContext.Provider, { value, children });
};

// src/hooks/authenticated/useAuthenticated.ts
import React3 from "react";
function useAuthenticated() {
  const context = React3.useContext(AppAuthenticatedContext);
  if (context === void 0) {
    throw new Error(
      "useAuthenticated must be used within a AppAuthenticatedContext.Provider"
    );
  }
  return context;
}

// src/hooks/authenticated/useAuthenticatedCurrentOffice.ts
function useAuthenticatedCurrentOffice() {
  const { userContext } = useAuthenticated();
  return userContext.currentOffice;
}

// src/hooks/authenticated/useAuthenticatedUser.ts
function useAuthenticatedUser() {
  const { userContext } = useAuthenticated();
  return userContext.authenticatedUser;
}

// src/hooks/resources/useResources.ts
import { useQuery as useQuery9 } from "@tanstack/react-query";
function useResources({
  officeId,
  types = ["workspace", "desk", "meeting_room"],
  parentId,
  filter,
  query,
  hierarchy = false,
  manage = false,
  limit = 4e3
}, {
  enabled = true
} = {
  enabled: true
}) {
  const { api } = useTacticHooks();
  types.sort();
  const { data: resources, error } = useQuery9(
    [
      "organizations.resourcesAll",
      officeId,
      types,
      parentId ? parentId : null,
      filter ? filter : null,
      void 0,
      null,
      query,
      void 0,
      limit,
      hierarchy,
      manage
    ],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.resourcesAll(
        officeId,
        types,
        parentId ? parentId : null,
        filter ? filter : null,
        void 0,
        null,
        query,
        void 0,
        limit,
        hierarchy,
        manage
      );
      return res.result;
    }),
    {
      enabled: enabled && officeId !== null
    }
  );
  const loading = !resources && !error;
  return { resources, error, loading };
}

// src/hooks/resources/useOfficeResources.ts
import { getStorageFloor } from "@gettactic/helpers/src/resources/getStorageFloor";
import { fillSchedules } from "@gettactic/helpers/src/schedules/fillSchedules";
import { useQuery as useQuery12 } from "@tanstack/react-query";
import { differenceInMinutes, format as format2 } from "date-fns";
import { useEffect as useEffect2, useMemo as useMemo5, useState } from "react";

// src/hooks/schedules/useMapSchedulable.ts
import { useQuery as useQuery10 } from "@tanstack/react-query";
function useMapSchedulable(officeId, parentId, startDateWithTimeStr, duration, hierarchy, enabled = true) {
  const { api } = useTacticHooks();
  const {
    data: schedulable,
    error,
    isLoading: loading
  } = useQuery10(
    [
      "organizations.schedulable",
      officeId,
      parentId,
      startDateWithTimeStr,
      duration
    ],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.schedulable(
        officeId,
        ["desk", "workspace", "meeting_room", "parking_lot", "parking_space"],
        parentId,
        [startDateWithTimeStr],
        duration ? duration.toString() : null,
        null,
        4e3,
        hierarchy
      );
      return res.result;
    }),
    {
      enabled: enabled && !!startDateWithTimeStr && !!officeId && !!duration
    }
  );
  return { schedulable, error, loading };
}

// src/hooks/users/useUserListPaginated.ts
import { useInfiniteQuery as useInfiniteQuery2 } from "@tanstack/react-query";
import { useMemo as useMemo3 } from "react";
function useUserListPaginated(query, limit, filters = {}, roles = [], managed = false) {
  const { api } = useTacticHooks();
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== null && typeof v !== "undefined") {
      params.set(k, v);
    }
  }
  if (query && query.trim() !== "") {
    params.set("query", query);
  }
  params.set("limit", limit.toString());
  roles.forEach((role) => params.append("role", role));
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery2(
    ["organizations.usersPaginated", params.toString(), managed],
    (_0) => __async(this, [_0], function* ({ pageParam: cursor }) {
      if (cursor) {
        params.set("cursor", cursor);
      }
      const res = yield api.client.organizations.usersPaginated(
        params,
        managed
      );
      return res.result;
    }),
    {
      getNextPageParam: (lastPage) => {
        var _a, _b;
        return ((_a = lastPage == null ? void 0 : lastPage.elements.length) != null ? _a : 0) < limit ? void 0 : (_b = lastPage == null ? void 0 : lastPage.cursor) != null ? _b : void 0;
      }
    }
  );
  const allPages = useMemo3(
    () => ((data == null ? void 0 : data.pages) || []).map((x) => {
      var _a;
      return (_a = x == null ? void 0 : x.elements) != null ? _a : [];
    }).reduce((arr, els) => {
      return arr.concat(els);
    }, []),
    [data]
  );
  return {
    data,
    allPages,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  };
}

// src/hooks/resources/useFloors.ts
import { useMemo as useMemo4 } from "react";

// src/hooks/resources/useAreas.ts
import { useQuery as useQuery11 } from "@tanstack/react-query";
function useAreas(officeId, parentId = null) {
  const { api } = useTacticHooks();
  const {
    data: areas,
    error,
    isLoading
  } = useQuery11(
    ["organizations.resourcesAll", officeId != null ? officeId : "", ["area"], parentId != null ? parentId : ""],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.resourcesAll(
        officeId != null ? officeId : "",
        ["area"],
        parentId
      );
      return res.result;
    }),
    {
      enabled: officeId !== null,
      keepPreviousData: true
    }
  );
  const loading = isLoading;
  return { areas, error, loading };
}

// src/hooks/resources/useFloors.ts
function useFloors(officeId, parentId = null) {
  const { areas } = useAreas(officeId, parentId);
  const floors = useMemo4(
    () => (areas == null ? void 0 : areas.elements) ? areas.elements.filter((x) => x.is_available) : void 0,
    [areas]
  );
  return { floors };
}

// src/hooks/resources/useOfficeResources.ts
function useOfficeResources(officeId, startDate, endDate, filterQuery, entireOffice, getAllMaps, _lastFloorId) {
  var _a;
  const { api } = useTacticHooks();
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();
  const startDateWithTimeStr = startDate ? format2(startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null;
  const startDateStr = startDate ? format2(startDate, "yyyy-MM-dd") : null;
  const duration = endDate && startDate ? differenceInMinutes(endDate, startDate) : null;
  let loading;
  const paginatedUsers = useUserListPaginated(filterQuery, 200);
  loading = !paginatedUsers.data;
  const { floors: allFloors } = useFloors(officeId, null);
  const [floor, setFloor] = useState(null);
  const loadFloor = () => {
    var _a2, _b, _c, _d;
    if (!allFloors) {
      return;
    }
    const lastFloorId = _lastFloorId || getStorageFloor();
    const withMap = getAllMaps ? allFloors : allFloors.filter((x) => !!x.map_url || !!x.map_data);
    const newFloor = (_d = (_c = (_b = (_a2 = withMap.find((x) => x.id === lastFloorId)) != null ? _a2 : withMap.find((x) => x.id === authenticatedUser.defaultAreaId)) != null ? _b : withMap.find((x) => x.id === (floor == null ? void 0 : floor.id))) != null ? _c : withMap[0]) != null ? _d : allFloors[0];
    if ((floor == null ? void 0 : floor.id) !== (newFloor == null ? void 0 : newFloor.id) || (floor == null ? void 0 : floor.map_data) !== (newFloor == null ? void 0 : newFloor.map_data)) {
      setFloor(newFloor);
    }
  };
  useEffect2(loadFloor, [
    allFloors,
    floor == null ? void 0 : floor.id,
    authenticatedUser,
    currentOffice.id
  ]);
  loading || (loading = typeof allFloors === "undefined");
  const floorExists = !!(allFloors == null ? void 0 : allFloors.find(
    (x) => x.id === (floor == null ? void 0 : floor.id) && floor.office_id === currentOffice.id
  ));
  const enableFetch = floorExists;
  const params = {
    office: officeId ? [officeId] : [],
    date: startDateStr != null ? startDateStr : "",
    type: ["desk", "workspace", "meeting_room", "parking_lot", "parking_space"],
    tz: currentOffice.time_zone,
    days: 1,
    resource: !entireOffice && (floor == null ? void 0 : floor.id) ? [floor == null ? void 0 : floor.id] : void 0
  };
  const { data: usersSchedule, refetch: refetchSchedules } = useQuery12(
    ["schedules", "occupied", params],
    () => __async(this, null, function* () {
      const res = yield api.client.schedules.allIndexed("occupied", params);
      return res.result;
    }),
    {
      enabled: enableFetch && officeId !== null
    }
  );
  const reload = () => __async(this, null, function* () {
    yield refetchSchedules();
    loadFloor();
  });
  loading || (loading = floorExists && !usersSchedule);
  const floorsWithMap = allFloors ? getAllMaps ? allFloors : allFloors.filter((x) => x.map_url) : void 0;
  const { resources } = useResources(
    {
      officeId,
      types: [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space"
      ],
      parentId: !entireOffice ? floor == null ? void 0 : floor.id : void 0,
      hierarchy: !entireOffice
    },
    {
      enabled: enableFetch && officeId !== null
    }
  );
  loading || (loading = floorExists && !resources);
  const { schedulable } = useMapSchedulable(
    officeId,
    !entireOffice ? (_a = floor == null ? void 0 : floor.id) != null ? _a : null : null,
    startDateWithTimeStr,
    duration,
    !entireOffice,
    enableFetch
  );
  loading || (loading = floorExists && !schedulable);
  const resourcesFilled = useMemo5(() => {
    if (!resources || !schedulable || !startDateStr) {
      return [];
    }
    return fillSchedules(
      resources.elements.map((r) => {
        const s = (schedulable || []).find((x) => x.id === r.id);
        return s ? __spreadProps(__spreadValues(__spreadValues({}, r), s), { label: r.label, description: r.description }) : __spreadProps(__spreadValues({}, r), { available_slots: 0, is_available: 0 });
      }),
      usersSchedule,
      startDateStr
    );
  }, [resources, schedulable, usersSchedule, startDateStr]);
  const floorsWithResources = useMemo5(() => {
    if (!resourcesFilled) {
      return void 0;
    }
    const list = floorsWithMap != null ? floorsWithMap : [];
    const hasOnlyOneFloor = list.length === 1;
    return list.map((floor2, number) => __spreadProps(__spreadValues({}, floor2), {
      number: number + 1,
      resources: resourcesFilled.filter((r) => hasOnlyOneFloor ? true : r.parent_id === floor2.id)
    }));
  }, [floorsWithMap, resourcesFilled, schedulable]);
  const usersAtOffice = useMemo5(() => {
    if (!resourcesFilled) {
      return void 0;
    }
    return resourcesFilled.reduce((arr, res) => {
      const users = (res.schedules || []).map((sc) => __spreadProps(__spreadValues({}, sc.user), {
        resourceId: sc.resource.id
      }));
      return arr.concat(users);
    }, []);
  }, [resourcesFilled]);
  return {
    loading,
    // use the schedulable data filled with
    // resources in use
    schedulable: resourcesFilled,
    floors: floorsWithMap,
    floorsWithResources,
    floor,
    setFloor,
    usersAtOffice,
    resources: resources ? resources.elements : void 0,
    reload
  };
}

// src/hooks/resources/useFloorsForOffices.ts
import { useQuery as useQuery13 } from "@tanstack/react-query";
function useFloorsForOffices(offices) {
  const { api } = useTacticHooks();
  return useQuery13(
    ["organizations.resourcesAll", "multi-floor", offices],
    () => __async(this, null, function* () {
      const promises = offices.map((office) => __async(this, null, function* () {
        var _a, _b;
        const res = yield api.client.organizations.resourcesAll(office, [
          "area"
        ]);
        return ((_a = res.result) == null ? void 0 : _a.elements) ? (_b = res.result) == null ? void 0 : _b.elements.filter((x) => x.is_available) : [];
      }));
      return (yield Promise.all(promises)).flat();
    })
  );
}

// src/hooks/schedules/useSortedSchedules.ts
import { sortSchedulesByStart } from "@gettactic/helpers/src/schedules/sortSchedulesByStart";
import React4 from "react";
function useSortedSchedules(data, filter = "all") {
  return React4.useMemo(() => {
    const noFilter = data != null ? data : [];
    return noFilter.filter(
      (x) => filter === "all" ? true : filter === "rooms" && x.resource.type === "meeting_room" || filter === "workspaces" && x.resource.type !== "meeting_room"
    ).sort(sortSchedulesByStart);
  }, [filter, data]);
}

// src/hooks/schedules/useDefaultOfficeHours.ts
import { ALL_WEEK_DAYS } from "@gettactic/api";
import { pad } from "@gettactic/helpers";
import { calculateHoursDifference } from "@gettactic/helpers/src/date-time/calculateHoursDifference";
import { getDefaultsHoursForOffice } from "@gettactic/helpers/src/schedules/getDefaultsHoursForOffice";
import { isWithinWorkingHours } from "@gettactic/helpers/src/schedules/isWithinWorkingHours";
import { useCallback as useCallback2 } from "react";

// src/hooks/hours/useOperationalHours.ts
import { useQuery as useQuery14 } from "@tanstack/react-query";
function useOperationalHours(office) {
  const { api } = useTacticHooks();
  return useQuery14(
    ["hours.rules", office == null ? void 0 : office.id],
    () => __async(this, null, function* () {
      var _a;
      const res = yield api.client.hours.weekdays((_a = office == null ? void 0 : office.id) != null ? _a : "");
      return res.result;
    }),
    {
      enabled: !!(office == null ? void 0 : office.id)
    }
  );
}

// src/hooks/schedules/useDefaultOfficeHours.ts
var WEEK_START_ON_MONDAY = Object.freeze([6, 0, 1, 2, 3, 4, 5]);
function useDefaultOfficeHours(officeId) {
  const workingHours = useOperationalHours(
    officeId ? { id: officeId } : void 0
  );
  const definedOfficeHour = getDefaultsHoursForOffice(officeId);
  return useCallback2(
    (forDay) => {
      var _a, _b, _c;
      if (!workingHours.isSuccess) {
        return void 0;
      }
      if (workingHours.isFetched && ((_a = workingHours.data) == null ? void 0 : _a.elements.length) === 0) {
        return definedOfficeHour;
      }
      const operational = (_c = (_b = workingHours.data) == null ? void 0 : _b.elements) != null ? _c : [];
      const dayOfWeekName = ALL_WEEK_DAYS[WEEK_START_ON_MONDAY[forDay.getDay()]];
      const currentWorkingHour = operational.find(
        (x) => {
          var _a2;
          return (_a2 = x.days) == null ? void 0 : _a2.find((xx) => xx.toLowerCase() === dayOfWeekName);
        }
      );
      const workingHoursOpenWindow = currentWorkingHour ? calculateHoursDifference(
        currentWorkingHour.start_time,
        currentWorkingHour.end_time
      ) : 0;
      const isOpen24 = workingHoursOpenWindow === 24;
      const definedHoursOpenWindow = calculateHoursDifference(
        `${pad(definedOfficeHour.startHour)}:${pad(
          definedOfficeHour.startMinutes
        )}:00.000`,
        `${pad(definedOfficeHour.endHour)}:${pad(
          definedOfficeHour.endMinutes
        )}:00.000`
      );
      if (!currentWorkingHour) {
        return definedOfficeHour;
      }
      const [startHours, startMinutes] = currentWorkingHour.start_time.split(":");
      const [endHours, endMinutes] = currentWorkingHour.end_time.split(":");
      const currentWHValues = {
        startHour: Number.parseInt(startHours),
        startMinutes: Number.parseInt(startMinutes),
        endHour: Number.parseInt(endHours),
        endMinutes: Number.parseInt(endMinutes),
        meetingRoomDuration: definedOfficeHour.meetingRoomDuration,
        deskDuration: definedOfficeHour.deskDuration
      };
      if (!isOpen24 && !isWithinWorkingHours(definedOfficeHour, currentWHValues)) {
        return currentWHValues;
      }
      if (isOpen24) {
        return definedOfficeHour;
      }
      return currentWHValues;
    },
    [workingHours.isSuccess, officeId]
  );
}

// src/hooks/users/useUserList.ts
import { useQuery as useQuery15 } from "@tanstack/react-query";
function useUserList(query = null, roles = null, cursor = null, managed = false) {
  const { api } = useTacticHooks();
  return useQuery15(
    ["organizations.usersWithOptions", query, roles, cursor, managed],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.usersWithOptions(
        query,
        roles,
        {
          cursor,
          limit: null
        },
        managed
      );
      return res.result;
    })
  );
}

// src/hooks/users/useUserListByIds.ts
import { useQuery as useQuery16 } from "@tanstack/react-query";
function useUserListByIds(ids) {
  const { api } = useTacticHooks();
  return useQuery16(["organizations.usersWithOptions", { ids }], () => __async(this, null, function* () {
    const res = yield api.client.organizations.usersById(ids);
    return res;
  }));
}

// src/hooks/colleagues/useColleagues.ts
import { useQuery as useQuery17 } from "@tanstack/react-query";
function useColleagues(userId) {
  const { api } = useTacticHooks();
  const {
    data: colleagues,
    error,
    isFetching
  } = useQuery17(["organizations.user.colleagues", userId], () => __async(this, null, function* () {
    const res = yield api.client.organizations.userColleagues(userId);
    return res.result;
  }));
  return { colleagues, error, isFetching };
}

// src/features/useIsPrivacyEnabled.ts
import { isHpe, isOrganizationAdmin } from "@gettactic/helpers";
var useIsPrivacyEnabled = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  return isHpe(authenticatedUser) && !isOrganizationAdmin(authenticatedUser);
};

// src/hooks/pages/useGetPageSectionsForOffice.ts
import React5 from "react";
import { findDivisionPathByOfficeId } from "@gettactic/helpers/src/divisions/findDivisionPathByOfficeId";
var useGetPageSectionsForOffice = (selectedOfficeId, options) => {
  const { data: divisions } = useDivisionsMenuQuery();
  const sections = usePageSectionsQuery();
  return React5.useMemo(() => {
    var _a;
    if (!sections || !selectedOfficeId && !(options == null ? void 0 : options.allowEmptyOffice)) {
      return [];
    }
    const pageSectionsRoot = sections || [];
    const divisionPath = findDivisionPathByOfficeId(
      ((_a = divisions == null ? void 0 : divisions.tree) == null ? void 0 : _a.children) || [],
      selectedOfficeId
    );
    const foundSections = [];
    if (pageSectionsRoot.length > 0) {
      pageSectionsRoot.forEach((section) => {
        if ((options == null ? void 0 : options.allowEmptyOffice) && !selectedOfficeId || section.division_id && (divisionPath == null ? void 0 : divisionPath.includes(section.division_id)) || section.office_id && section.office_id === selectedOfficeId) {
          foundSections.push(section);
        }
      });
    }
    if (options == null ? void 0 : options.parentSectionId) {
      return foundSections.filter(
        (section) => section.parent_id === options.parentSectionId
      );
    }
    if (options == null ? void 0 : options.onlyRootSections) {
      const rootSections = foundSections.filter(
        (section) => !section.parent_id
      );
      if (options.childrenAsRootIfSingleRoot && rootSections.length === 1) {
        return foundSections.filter((section) => section.parent_id === foundSections[0].id).map((section) => {
          return __spreadProps(__spreadValues({}, section), {
            parent: rootSections[0]
          });
        });
      }
      return rootSections;
    }
    return foundSections;
  }, [
    sections,
    selectedOfficeId,
    options == null ? void 0 : options.parentSectionId,
    options == null ? void 0 : options.allowEmptyOffice,
    options == null ? void 0 : options.onlyRootSections,
    options == null ? void 0 : options.childrenAsRootIfSingleRoot
  ]);
};
export {
  AppAuthenticatedContext,
  AppAuthenticatedProvider,
  TacticHooksProvider,
  useAllApplicablePolicies,
  useApplicableRemotePolicy,
  useAreas,
  useAttendanceRequests,
  useAttendanceRequestsWeek,
  useAuthenticated,
  useAuthenticatedCurrentOffice,
  useAuthenticatedUser,
  useColleagues,
  useDefaultOfficeHours,
  useDivisionsMenuQuery,
  useDivisionsQuery,
  useFloors,
  useFloorsForOffices,
  useGetPageSectionsForOffice,
  useI18n,
  useIsPrivacyEnabled,
  useMapSchedulable,
  useOfficeResources,
  useOfficesQuery,
  useOperationalHours,
  useOrganizationQuery,
  usePageSectionsQuery,
  usePagesQuery,
  useResources,
  useSortedSchedules,
  useTacticHooks,
  useUserList,
  useUserListByIds,
  useUserListPaginated,
  useWorkingLocations
};
//# sourceMappingURL=index.mjs.map